// Material Inward register — RBM's own columns (sample, 2026-10-24):
//   Date · Supplier Name · Item · Total Qty · Pack Size · Invoice No. · Transport
// Server: src/api-inventory/material-inward.js. Saving posts the stock receipt.
import { useEffect, useMemo, useState } from 'react';
import { ArrowDownToLine, Ban, Download, FlaskConical, Link2, Plus, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Select } from '@/shared/components/ui/Select';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { localToday } from '@/shared/utils/utils';
import { useUser } from '@/shared/hooks/useAuth';
import {
  useMaterialInwards, useMaterialInwardAction, useTankRegister, materialErrorText, downloadCsv,
  type MaterialCategory, type MaterialInward,
} from '../hooks/useMaterials';
import { useRecordQc, uploadFile } from '../hooks/useStockControl';
import { FieldLabel, FilterChips, ItemNameInput, ItemSearchInput, Panel, Pill, SupplierPicker, fmtDay, fmtQty, type PillTone } from './materialShared';

const CATEGORY_LABEL: Record<MaterialCategory, string> = {
  raw_material: 'Raw Material', consumables: 'Consumables', packaging_material: 'Packaging Material', return: 'Return',
};
const CATEGORY_TONE: Record<MaterialCategory, PillTone> = { raw_material: 'primary', consumables: 'info', packaging_material: 'warning', return: 'danger' };
const TRANSPORT_LABEL: Record<string, string> = { self: 'Self', supplier: 'Supplier vehicle', transporter: 'Transporter', courier: 'Courier' };
const PACK_UNITS = ['KG', 'LTR', 'ML', 'GM', 'NOS'];

const monthStart = () => { const d = new Date(); return localToday(new Date(d.getFullYear(), d.getMonth(), 1)); };
const packText = (r: Pick<MaterialInward, 'packSize' | 'packUnit' | 'packCount'>) => (r.packSize > 0 && r.packCount > 0 ? `${fmtQty(r.packSize)} ${r.packUnit} × ${fmtQty(r.packCount)}` : '—');
const transportText = (r: MaterialInward) => [TRANSPORT_LABEL[r.transportMode] || r.transportMode, r.transporterName, r.vehicleNumber].filter(Boolean).join(' · ');
const isApprover = (role?: string) => ['superadmin', 'owner', 'admin'].includes(String(role || ''));

export function MaterialInwardTab({ openNew = 0 }: { openNew?: number }) {
  const user = useUser();
  const admin = isApprover(user?.role);
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(localToday());
  const [category, setCategory] = useState<'all' | MaterialCategory>('all');
  const [q, setQ] = useState('');
  const [recording, setRecording] = useState(false);
  const [cancelling, setCancelling] = useState<MaterialInward | null>(null);
  const [linking, setLinking] = useState<MaterialInward | null>(null);
  const [qcFor, setQcFor] = useState<MaterialInward | null>(null);
  const [qcOnly, setQcOnly] = useState(false);
  const { data, isLoading } = useMaterialInwards({ from, to, category, q });
  useEffect(() => { if (openNew) setRecording(true); }, [openNew]);

  const rows = (data?.inwards || []).filter((r) => !qcOnly || r.qcStatus === 'pending');
  const exportCsv = () => downloadCsv(`material-inward-${from}-to-${to}.csv`,
    ['Date', 'Supplier Name', 'Item', 'Total Qty', 'Pack Size', 'Invoice No.', 'Transport', 'Inward No.', 'Category', 'Unit', 'Vehicle No.', 'Destination', 'Batch', 'Expiry', 'QC', 'Accepted', 'Rate', 'Status', 'Received by'],
    rows.map((r) => [
      fmtDay(r.dateKey), r.supplierName, r.itemName, r.totalQty, r.packSize && r.packCount ? `${r.packSize}x${r.packCount}` : '', r.invoiceNumber,
      (TRANSPORT_LABEL[r.transportMode] || r.transportMode).toUpperCase(), r.inwardNumber, CATEGORY_LABEL[r.category], r.packUnit, r.vehicleNumber,
      r.destination === 'tank' ? `Tank ${r.tankCode}` : 'Warehouse', r.batchNumber, fmtDay(r.expiryDate), data?.qcStatuses?.[r.qcStatus] || '', r.qc?.acceptedQty ?? '', r.rate || '', r.status === 'cancelled' ? 'Cancelled' : 'Received', r.receivedByName,
    ]));

  return (
    <div className="space-y-4">
      <Panel
        title="Material inward"
        subtitle="Every receipt at the gate: raw material, consumables, packaging and returns. Raw material waits for QC; the accepted quantity goes into stock."
        actions={<>
          <Button variant="outline" onClick={exportCsv} disabled={!rows.length}><Download className="h-4 w-4" /> Excel</Button>
          <Button onClick={() => setRecording(true)}><Plus className="h-4 w-4" /> Record inward</Button>
        </>}
      >
        <div className="flex flex-wrap items-end justify-between gap-3 px-5 py-3">
          <FilterChips<'all' | MaterialCategory>
            value={category}
            onChange={setCategory}
            options={[
              { value: 'all', label: 'All', count: data?.summary.count },
              ...(Object.keys(CATEGORY_LABEL) as MaterialCategory[]).map((k) => ({ value: k, label: CATEGORY_LABEL[k], count: data?.summary.byCategory?.[k] ?? 0 })),
            ]}
          />
          <div className="flex flex-wrap items-end gap-2">
            {!!data?.summary.qcPending && (
              <button type="button" onClick={() => setQcOnly((v) => !v)} className={`inline-flex h-9 items-center gap-1.5 rounded-lg border px-3 text-body-sm font-semibold ${qcOnly ? 'border-warning bg-warning text-white' : 'border-warning/40 bg-warning/10 text-warning-text'}`}>
                <FlaskConical className="h-4 w-4" /> QC pending · {data.summary.qcPending}
              </button>
            )}
            <div className="w-40"><Input type="date" label="From" value={from} max={to} onChange={(e) => setFrom(e.target.value)} /></div>
            <div className="w-40"><Input type="date" label="To" value={to} min={from} max={localToday()} onChange={(e) => setTo(e.target.value)} /></div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-subtle" aria-hidden />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Supplier, item, invoice…" className="h-9 w-56 rounded-lg border border-input bg-surface pl-8 pr-3 text-body-sm outline-none focus:border-primary" />
            </div>
          </div>
        </div>
        {!!data?.summary.notInStock && (
          <p className="mx-5 mb-3 rounded-lg border border-warning/25 bg-warning/10 px-3 py-2 text-body-sm text-warning-text">
            {data.summary.notInStock} entr{data.summary.notInStock === 1 ? 'y is' : 'ies are'} not in stock yet: the item was typed, not picked from the Item Master.{admin ? ' Use "Link item" to post the stock.' : ' Admin can link the item.'}
          </p>
        )}

        {isLoading ? <div className="p-5"><Skeleton className="h-40" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1080px] text-body-sm">
              <thead className="border-y border-border bg-surface-container-low/60 text-left text-label-caps uppercase text-on-surface-variant">
                <tr>
                  <th className="px-5 py-2.5">Date</th>
                  <th className="px-3 py-2.5">Supplier name</th>
                  <th className="px-3 py-2.5">Item</th>
                  <th className="px-3 py-2.5 text-right">Total qty</th>
                  <th className="px-3 py-2.5">Pack size</th>
                  <th className="px-3 py-2.5">Invoice no.</th>
                  <th className="px-3 py-2.5">Transport</th>
                  <th className="px-3 py-2.5">Stock</th>
                  <th className="px-5 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((r) => (
                  <tr key={r._id} className={r.status === 'cancelled' ? 'opacity-55' : 'hover:bg-surface-container-low/60'}>
                    <td className="whitespace-nowrap px-5 py-3">
                      <div className="text-on-surface">{fmtDay(r.dateKey)}</div>
                      <div className="whitespace-nowrap font-mono text-data-xs text-on-surface-subtle">{r.inwardNumber}</div>
                    </td>
                    <td className="px-3 py-3 font-medium text-on-surface">{r.supplierName}</td>
                    <td className="px-3 py-3">
                      <div className="text-on-surface">{r.itemName}</div>
                      <Pill tone={CATEGORY_TONE[r.category]} className="mt-1">{CATEGORY_LABEL[r.category]}</Pill>
                    </td>
                    <td className="px-3 py-3 text-right font-mono tabular-nums text-on-surface">{fmtQty(r.totalQty)} <span className="text-on-surface-subtle">{r.packUnit}</span></td>
                    <td className="px-3 py-3 font-mono text-on-surface-variant">{packText(r)}</td>
                    <td className="px-3 py-3 text-on-surface-variant">{r.invoiceNumber || '—'}</td>
                    <td className="px-3 py-3 text-on-surface-variant">{transportText(r)}{r.destination === 'tank' && <div className="text-data-xs">into tank {r.tankCode}</div>}</td>
                    <td className="px-3 py-3">
                      {r.status === 'cancelled' ? <Pill tone="neutral" className="line-through">Cancelled</Pill>
                        : r.qcStatus === 'pending' ? (data?.canQc
                          ? <Button size="sm" variant="outline" onClick={() => setQcFor(r)}><FlaskConical className="h-3.5 w-3.5" /> Record QC</Button>
                          : <Pill tone="warning">QC pending</Pill>)
                          : r.qcStatus === 'rejected' ? <Pill tone="danger" title={r.qc?.remarks}>QC rejected</Pill>
                            : r.stockPosted ? <Pill tone="success" title={r.qc?.checkedByName ? `QC by ${r.qc.checkedByName}${r.qc.remarks ? `: ${r.qc.remarks}` : ''}` : undefined}>+{fmtQty(r.stockQuantity)} {r.stockBasis === 'packs' ? 'packs' : r.packUnit}{r.qcStatus === 'partly_accepted' ? ' (part)' : ''}</Pill>
                              : <Pill tone="warning">Not in stock</Pill>}
                      <div className="mt-1 font-mono text-data-xs text-on-surface-subtle">{r.batchNumber && r.batchNumber !== r.inwardNumber ? `Batch ${r.batchNumber}` : ''}{r.expiryDate ? `${r.batchNumber && r.batchNumber !== r.inwardNumber ? ' · ' : ''}exp ${fmtDay(r.expiryDate)}` : ''}</div>
                      {(r.quantityKg || r.quantityLitres) && r.packUnit !== 'NOS' ? <div className="font-mono text-data-xs text-on-surface-subtle">{r.packUnit === 'KG' ? `≈ ${fmtQty(r.quantityLitres)} LTR` : `≈ ${fmtQty(r.quantityKg)} KG`}</div> : null}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {admin && r.status !== 'cancelled' && (
                        <div className="flex justify-end gap-1">
                          {!r.stockPosted && r.qcStatus !== 'pending' && r.qcStatus !== 'rejected' && <Button size="sm" variant="outline" className="whitespace-nowrap" onClick={() => setLinking(r)}><Link2 className="h-3.5 w-3.5" /> Link item</Button>}
                          <Button size="sm" variant="ghost" onClick={() => setCancelling(r)} aria-label={`Cancel ${r.inwardNumber}`}><Ban className="h-3.5 w-3.5" /></Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!rows.length && (
              <div className="flex flex-col items-center gap-2 px-5 py-12 text-center">
                <ArrowDownToLine className="h-8 w-8 text-on-surface-subtle" aria-hidden />
                <p className="text-body-md font-medium text-on-surface">No receipts in this period</p>
                <p className="text-body-sm text-on-surface-variant">Record material as it arrives at the gate.</p>
              </div>
            )}
          </div>
        )}
      </Panel>

      <RecordInwardModal open={recording} onClose={() => setRecording(false)} />
      <CancelInwardModal inward={cancelling} onClose={() => setCancelling(null)} />
      <LinkItemModal inward={linking} onClose={() => setLinking(null)} />
      <QcModal inward={qcFor} onClose={() => setQcFor(null)} />
    </div>
  );
}

const emptyForm = () => ({
  date: localToday(), category: 'raw_material' as MaterialCategory, supplierId: '', supplierName: '', productId: null as string | null, itemName: '',
  packSize: '', packUnit: 'KG', packCount: '', totalQty: '', invoiceNumber: '', transportMode: 'self', transporterName: '', vehicleNumber: '',
  destination: 'warehouse' as 'warehouse' | 'tank', tankCode: '', rate: '', batchNumber: '', expiryDate: '', density: '', returnAgainst: '', remarks: '',
});

function RecordInwardModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const action = useMaterialInwardAction();
  const [f, setF] = useState(emptyForm);
  const today = localToday();
  const { data: tankData } = useTankRegister(today, today);
  useEffect(() => { if (open) setF(emptyForm()); }, [open]);
  const set = (p: Partial<ReturnType<typeof emptyForm>>) => setF((x) => ({ ...x, ...p }));
  // 180 × 10 fills the total unless the user typed their own.
  const autoTotal = Number(f.packSize) > 0 && Number(f.packCount) > 0 ? Math.round(Number(f.packSize) * Number(f.packCount) * 1000) / 1000 : null;
  const total = f.totalQty !== '' ? Number(f.totalQty) : autoTotal;
  const isReturn = f.category === 'return';
  const ready = f.itemName.trim() && (isReturn ? f.supplierName.trim() : f.supplierId) && total && total > 0 && (f.destination !== 'tank' || f.tankCode);
  const stockHint = useMemo(() => {
    if (f.category === 'raw_material') return 'Raw material: saved as QC pending; stock goes in once QC accepts it.';
    if (!f.productId) return 'Saved under the name typed — matched to the item already in the list, or added as a new item — and stock goes in.';
    if (/\b(BULK|LOOSE)\b/i.test(f.itemName)) return `Stock rises by ${fmtQty(total)} ${f.packUnit}.`;
    if (Number(f.packCount) > 0) return `Stock rises by ${fmtQty(Number(f.packCount))} packs of ${f.itemName}.`;
    return `Stock rises by ${fmtQty(total)}.`;
  }, [f.productId, f.itemName, f.packCount, f.packUnit, f.category, total]);

  const submit = async () => {
    try {
      const res = await action.mutateAsync({ action: 'create', body: { ...f, totalQty: total, packSize: Number(f.packSize) || 0, packCount: Number(f.packCount) || 0, rate: Number(f.rate) || 0, density: Number(f.density) || 0, expiryDate: f.expiryDate || undefined } });
      toast.success(`${res.inward.inwardNumber} recorded`);
      if (res.itemAdded) toast(`New item added to the list: ${res.inward.itemName}`, { icon: '➕', duration: 6000 });
      if (res.stockWarning) toast(res.stockWarning, { icon: '⚠️', duration: 7000 });
      onClose();
    } catch (err) {
      toast.error(materialErrorText(err, 'Could not record the inward.'));
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="xl" title="Record material inward" footer={(
      <>
        <span className="mr-auto text-body-sm text-on-surface-variant">{stockHint}</span>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} loading={action.isPending} disabled={!ready}>Save inward</Button>
      </>
    )}>
      <div className="space-y-4">
        <div>
          <FieldLabel>Category</FieldLabel>
          <FilterChips<MaterialCategory> value={f.category} onChange={(category) => set({ category })} options={(Object.keys(CATEGORY_LABEL) as MaterialCategory[]).map((k) => ({ value: k, label: CATEGORY_LABEL[k] }))} />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Input label="Date" type="date" value={f.date} max={localToday()} onChange={(e) => set({ date: e.target.value })} />
          <div className="sm:col-span-2">
            <FieldLabel required>{isReturn ? 'Returned by (customer)' : 'Supplier name'}</FieldLabel>
            {isReturn
              ? <input value={f.supplierName} onChange={(e) => set({ supplierName: e.target.value })} placeholder="Customer name" className="h-9 w-full rounded-lg border border-input bg-surface px-3 text-body-sm outline-none focus:border-primary" />
              : <SupplierPicker value={f.supplierId} onChange={(s) => set({ supplierId: s?._id || '', supplierName: s?.name || '' })} />}
          </div>
        </div>
        <div>
          <FieldLabel required>Item</FieldLabel>
          <ItemNameInput value={f.itemName} onChange={(n) => set({ itemName: n.itemName, productId: n.productId })} placeholder="e.g. MP3 Grease, SN 70 base oil, 210L drum" />
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <Input label="Pack size" type="number" min="0" step="any" value={f.packSize} onChange={(e) => set({ packSize: e.target.value })} placeholder="180" />
          <label className="block"><FieldLabel>Unit</FieldLabel><Select value={f.packUnit} onChange={(e) => set({ packUnit: e.target.value })} options={PACK_UNITS.map((u) => ({ value: u, label: u }))} /></label>
          <Input label="No. of packs" type="number" min="0" step="any" value={f.packCount} onChange={(e) => set({ packCount: e.target.value })} placeholder="10" />
          <Input label="Total qty" type="number" min="0" step="any" value={f.totalQty} onChange={(e) => set({ totalQty: e.target.value })} placeholder={autoTotal ? String(autoTotal) : '1800'} hint={autoTotal && f.totalQty === '' ? `= ${fmtQty(autoTotal)} ${f.packUnit}` : undefined} />
          <Input label="Rate (optional)" type="number" min="0" step="any" value={f.rate} onChange={(e) => set({ rate: e.target.value })} placeholder="₹ per pack" />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Input label={isReturn ? 'Our invoice / challan no.' : 'Supplier invoice no.'} value={isReturn ? f.returnAgainst : f.invoiceNumber} onChange={(e) => (isReturn ? set({ returnAgainst: e.target.value }) : set({ invoiceNumber: e.target.value }))} placeholder="12345" />
          <label className="block"><FieldLabel>Transport</FieldLabel>
            <Select value={f.transportMode} onChange={(e) => set({ transportMode: e.target.value })} options={Object.entries(TRANSPORT_LABEL).map(([value, label]) => ({ value, label }))} />
          </label>
          <Input label="Vehicle no." value={f.vehicleNumber} onChange={(e) => set({ vehicleNumber: e.target.value.toUpperCase() })} placeholder="MH 12 AB 1234" />
        </div>
        {f.transportMode !== 'self' && (
          <Input label={f.transportMode === 'courier' ? 'Courier name' : 'Transporter name'} value={f.transporterName} onChange={(e) => set({ transporterName: e.target.value })} />
        )}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="block"><FieldLabel>Stored in</FieldLabel>
            <Select value={f.destination} onChange={(e) => set({ destination: e.target.value as 'warehouse' | 'tank' })} options={[{ value: 'warehouse', label: 'Store / warehouse' }, { value: 'tank', label: 'Tank (bulk oil)' }]} />
          </label>
          {f.destination === 'tank' && (
            <label className="block"><FieldLabel required>Tank</FieldLabel>
              <Select value={f.tankCode} onChange={(e) => set({ tankCode: e.target.value })} options={[{ value: '', label: 'Choose tank' }, ...(tankData?.tanks || []).map((t) => ({ value: t.code, label: `${t.code} · ${t.groupLabel}` }))]} />
            </label>
          )}
          <Input label="Supplier batch no." value={f.batchNumber} onChange={(e) => set({ batchNumber: e.target.value })} hint="Blank: the inward number is the batch" />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Input label="Expiry date (optional)" type="date" value={f.expiryDate} min={f.date} onChange={(e) => set({ expiryDate: e.target.value })} hint="Blank: from the item shelf life" />
          {(f.packUnit === 'KG' || f.packUnit === 'LTR') && (
            <Input label="Density kg/L (optional)" type="number" min="0" step="0.001" value={f.density} onChange={(e) => set({ density: e.target.value })} placeholder="0.86"
              hint={Number(f.density) > 0 && total ? (f.packUnit === 'KG' ? `≈ ${fmtQty(Math.round((total / Number(f.density)) * 100) / 100)} LTR` : `≈ ${fmtQty(Math.round(total * Number(f.density) * 100) / 100)} KG`) : 'Converts KG and litres'} />
          )}
        </div>
        <label className="block"><FieldLabel>Remarks</FieldLabel>
          <textarea value={f.remarks} onChange={(e) => set({ remarks: e.target.value })} rows={2} className="w-full rounded-lg border border-input bg-surface px-3 py-2 text-body-sm outline-none focus:border-primary" />
        </label>
      </div>
    </Modal>
  );
}

function CancelInwardModal({ inward, onClose }: { inward: MaterialInward | null; onClose: () => void }) {
  const action = useMaterialInwardAction();
  const [reason, setReason] = useState('');
  useEffect(() => { setReason(''); }, [inward?._id]);
  if (!inward) return null;
  const submit = async () => {
    try {
      await action.mutateAsync({ id: inward._id, action: 'cancel', body: { reason } });
      toast.success(`${inward.inwardNumber} cancelled${inward.stockPosted ? ' and stock reversed' : ''}`);
      onClose();
    } catch (err) {
      toast.error(materialErrorText(err, 'Could not cancel.'));
    }
  };
  return (
    <Modal open onClose={onClose} size="sm" title={`Cancel ${inward.inwardNumber}?`} footer={(
      <><Button variant="outline" onClick={onClose}>Keep it</Button><Button variant="danger" onClick={submit} loading={action.isPending} disabled={!reason.trim()}>Cancel entry</Button></>
    )}>
      <p className="mb-3 text-body-sm text-on-surface-variant">
        {fmtQty(inward.totalQty)} {inward.packUnit} {inward.itemName} from {inward.supplierName}.
        {inward.stockPosted ? ` The ${fmtQty(inward.stockQuantity)} added to stock will be taken back out.` : ''}
      </p>
      <Input label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus placeholder="e.g. entered twice" />
    </Modal>
  );
}

function LinkItemModal({ inward, onClose }: { inward: MaterialInward | null; onClose: () => void }) {
  const action = useMaterialInwardAction();
  const [pick, setPick] = useState<{ itemName: string; productId: string | null }>({ itemName: '', productId: null });
  useEffect(() => { setPick({ itemName: inward?.itemName || '', productId: null }); }, [inward?._id, inward?.itemName]);
  if (!inward) return null;
  const submit = async () => {
    try {
      await action.mutateAsync({ id: inward._id, action: 'link-item', body: { productId: pick.productId } });
      toast.success('Item linked and stock posted');
      onClose();
    } catch (err) {
      toast.error(materialErrorText(err, 'Could not link the item.'));
    }
  };
  return (
    <Modal open onClose={onClose} size="md" title={`Link item — ${inward.inwardNumber}`} footer={(
      <><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} loading={action.isPending} disabled={!pick.productId}>Link and post stock</Button></>
    )}>
      <p className="mb-3 text-body-sm text-on-surface-variant">Typed as "{inward.itemName}". Choose the matching item from the Item Master.</p>
      <ItemSearchInput value={pick.itemName} productId={pick.productId} onChange={(n) => setPick({ itemName: n.itemName, productId: n.productId })} autoFocus />
    </Modal>
  );
}

function QcModal({ inward, onClose }: { inward: MaterialInward | null; onClose: () => void }) {
  const record = useRecordQc();
  const blankQc = { result: 'accepted' as 'accepted' | 'partly_accepted' | 'rejected', acceptedQty: '', density: '', viscosity40: '', appearance: '', waterPpm: '', flashPointC: '', remarks: '', coaUrl: '', coaName: '' };
  const [f, setF] = useState(blankQc);
  const [uploading, setUploading] = useState(false);
  useEffect(() => { setF(blankQc); }, [inward?._id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!inward) return null;
  const set = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  const submit = async () => {
    try {
      const res = await record.mutateAsync({ id: inward._id, ...f });
      toast.success(f.result === 'rejected' ? 'Rejected — not added to stock' : `QC recorded; ${fmtQty(res.inward.stockQuantity)} added to stock`);
      if (res.stockWarning) toast(res.stockWarning, { icon: '⚠️', duration: 7000 });
      onClose();
    } catch (err) { toast.error(materialErrorText(err, 'Could not record QC.')); }
  };
  const attach = async (file: File) => {
    setUploading(true);
    try { set({ coaUrl: await uploadFile(file), coaName: file.name }); } catch { toast.error('Upload failed.'); } finally { setUploading(false); }
  };
  return (
    <Modal open onClose={onClose} size="lg" title={`QC — ${inward.inwardNumber}`} footer={(
      <><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} loading={record.isPending} variant={f.result === 'rejected' ? 'danger' : 'primary'}>Save QC result</Button></>
    )}>
      <div className="space-y-4">
        <p className="text-body-sm text-on-surface-variant">{fmtQty(inward.totalQty)} {inward.packUnit} {inward.itemName} from {inward.supplierName}{inward.batchNumber ? ` · batch ${inward.batchNumber}` : ''}.</p>
        <div>
          <FieldLabel>Result</FieldLabel>
          <FilterChips<typeof f.result> value={f.result} onChange={(result) => set({ result })} options={[{ value: 'accepted', label: 'Accepted' }, { value: 'partly_accepted', label: 'Partly accepted' }, { value: 'rejected', label: 'Rejected' }]} />
        </div>
        {f.result === 'partly_accepted' && (
          <Input label={`Accepted quantity (of ${fmtQty(inward.totalQty)} ${inward.packUnit})`} type="number" min="0" step="any" value={f.acceptedQty} onChange={(e) => set({ acceptedQty: e.target.value })} />
        )}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Input label="Density kg/L" type="number" step="0.001" value={f.density} onChange={(e) => set({ density: e.target.value })} placeholder="0.86" />
          <Input label="Viscosity @40°C (cSt)" type="number" step="any" value={f.viscosity40} onChange={(e) => set({ viscosity40: e.target.value })} />
          <Input label="Water (ppm)" type="number" step="any" value={f.waterPpm} onChange={(e) => set({ waterPpm: e.target.value })} />
          <Input label="Flash point (°C)" type="number" step="any" value={f.flashPointC} onChange={(e) => set({ flashPointC: e.target.value })} />
        </div>
        <Input label="Appearance / colour" value={f.appearance} onChange={(e) => set({ appearance: e.target.value })} placeholder="Clear & bright" />
        <label className="block"><FieldLabel required={f.result !== 'accepted'}>Remarks</FieldLabel>
          <textarea value={f.remarks} onChange={(e) => set({ remarks: e.target.value })} rows={2} className="w-full rounded-lg border border-input bg-surface px-3 py-2 text-body-sm outline-none focus:border-primary" placeholder={f.result === 'accepted' ? 'Optional' : 'Why it was rejected'} />
        </label>
        <div>
          <FieldLabel>Supplier Certificate of Analysis (CoA)</FieldLabel>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-body-sm text-on-surface-variant hover:border-primary/40">
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) attach(file); }} />
            {uploading ? 'Uploading…' : f.coaName ? `Attached: ${f.coaName}` : 'Attach PDF or photo'}
          </label>
        </div>
      </div>
    </Modal>
  );
}

export default MaterialInwardTab;
